<template>
  <v-dialog v-model="open" max-width="520">
    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 font-weight-bold pt-5 px-6 pb-1">
        Send {{ documentLabel }} to customer
      </v-card-title>
      <v-card-subtitle class="px-6 pb-3 text-wrap">
        {{ customerName || 'The customer' }} gets the PDF by email and a link
        {{ hasBalance ? 'to view and pay online' : 'to view it online' }}.
      </v-card-subtitle>

      <v-card-text class="px-6 pt-0">
        <v-progress-linear v-if="loadingOptions" class="mb-4" color="primary" indeterminate/>

        <v-alert v-if="loadError" class="mb-4" density="compact" type="error" variant="tonal">{{ loadError }}</v-alert>

        <template v-else-if="!loadingOptions">
          <!-- Email -->
          <div class="send-channel">
            <v-checkbox v-model="viaEmail" color="primary" density="compact" hide-details label="Email with PDF attached"/>
            <v-text-field
              v-model="email"
              :disabled="!viaEmail"
              :error-messages="fieldErrors.email"
              class="mt-1"
              density="compact"
              label="To"
              placeholder="customer@example.com"
              type="email"
              variant="outlined"
            />
            <div class="text-caption text-medium-emphasis send-channel__meta">{{ allowanceText('email') }}</div>
          </div>

          <!-- SMS -->
          <div class="send-channel mt-3">
            <v-checkbox
              v-model="viaSms"
              :disabled="!options?.phone || options?.sms_opted_out"
              color="primary"
              density="compact"
              hide-details
              label="Text message with the link"
            />
            <div :class="{'text-medium-emphasis': !viaSms}" class="text-body-2 mt-1">
              <template v-if="!options?.phone">No phone number on file for this customer.</template>
              <template v-else-if="options?.sms_opted_out">{{ options.phone }} has opted out of texts.</template>
              <template v-else>To {{ options.phone }}</template>
            </div>
            <div v-if="viaSms" class="send-sms-preview text-caption mt-2">{{ smsPreview }}</div>
            <div v-if="fieldErrors.phone" class="text-caption text-error mt-1">{{ fieldErrors.phone }}</div>
            <div class="text-caption text-medium-emphasis send-channel__meta">{{ allowanceText('sms') }}</div>
          </div>

          <v-textarea
            v-model="note"
            :disabled="!viaEmail"
            class="mt-4"
            counter="500"
            density="compact"
            label="Add a note to the email (optional)"
            maxlength="500"
            rows="2"
            variant="outlined"
          />

          <v-alert v-if="submitError" class="mt-2" density="compact" type="warning" variant="tonal">{{
              submitError
            }}
          </v-alert>
          <div v-if="options?.sent_at" class="text-caption text-medium-emphasis mt-2">
            Last sent {{ formatSentAt(options.sent_at) }}.
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-spacer/>
        <v-btn variant="text" @click="open = false">{{ $t('cancel') }}</v-btn>
        <v-btn
          :disabled="!canSend"
          :loading="sending"
          color="primary"
          prepend-icon="mdi-send"
          variant="flat"
          @click="send()"
        >
          Send
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import {computed, ref, watch} from 'vue';
import axios, {isAxiosError} from 'axios';
import dayjs from 'dayjs';
import {push} from 'notivue';

type Allowance = { cooldown_seconds: number; remaining_hour: number; remaining_day: number };
type DeliveryOptions = {
  email: string | null;
  phone: string | null;
  sms_opted_out: boolean;
  link: string;
  sent_at: string | null;
  allowance: { email: Allowance; sms: Allowance };
};

const props = defineProps<{
  documentUuid: string;
  documentLabel: string;
  customerName?: string | null;
  companyName?: string | null;
  totalLabel?: string | null;
  hasBalance?: boolean;
}>();

const emit = defineEmits<{ sent: [] }>();

const open = ref(false);
const loadingOptions = ref(false);
const loadError = ref<string | null>(null);
const options = ref<DeliveryOptions | null>(null);
const viaEmail = ref(true);
const viaSms = ref(false);
const email = ref('');
const note = ref('');
const sending = ref(false);
const submitError = ref<string | null>(null);
const fieldErrors = ref<{ email?: string; phone?: string }>({});

const canSend = computed(() => {
  if (sending.value || loadingOptions.value || !options.value) {
    return false;
  }
  if (!viaEmail.value && !viaSms.value) {
    return false;
  }
  if (viaEmail.value && !email.value.trim()) {
    return false;
  }
  return true;
});

const smsPreview = computed(() => {
  const verb = props.hasBalance ? 'View & pay' : 'View';
  const total = props.totalLabel ? ` (${props.totalLabel})` : '';
  return `${props.companyName ?? 'Your company'}: your ${props.documentLabel}${total} is ready. ${verb}: ${options.value?.link ?? '…'}`;
});

function allowanceText(channel: 'email' | 'sms'): string {
  const a = options.value?.allowance?.[channel];
  if (!a) {
    return '';
  }
  if (a.cooldown_seconds > 0) {
    return `Just sent. Available again in ${Math.ceil(a.cooldown_seconds / 60)} min.`;
  }
  if (a.remaining_hour <= 0) {
    return 'Hourly limit reached for this document.';
  }
  return `${a.remaining_hour} left this hour, ${a.remaining_day} today.`;
}

function formatSentAt(value: string): string {
  return dayjs(value).format('MMM D, YYYY h:mm a');
}

async function loadOptions(): Promise<void> {
  loadingOptions.value = true;
  loadError.value = null;
  submitError.value = null;
  fieldErrors.value = {};

  try {
    const {data} = await axios.get<DeliveryOptions>(`/v1/order-documents/${props.documentUuid}/delivery`);
    options.value = data;
    email.value = data.email ?? '';
    viaEmail.value = Boolean(data.email);
    viaSms.value = false;
  } catch (error) {
    console.error(error);
    loadError.value = 'Couldn\'t load the customer\'s contact details. Close and try again.';
  } finally {
    loadingOptions.value = false;
  }
}

async function send(): Promise<void> {
  sending.value = true;
  submitError.value = null;
  fieldErrors.value = {};

  const channels: string[] = [];
  if (viaEmail.value) channels.push('email');
  if (viaSms.value) channels.push('sms');

  try {
    const {data} = await axios.post(`/v1/order-documents/${props.documentUuid}/deliver`, {
      channels,
      email: viaEmail.value ? email.value.trim() : undefined,
      note: note.value.trim() || undefined,
    });

    const parts: string[] = [];
    if (data.delivery?.email) parts.push(`emailed to ${data.delivery.email}`);
    if (data.delivery?.phone) parts.push(`texted to ${data.delivery.phone}`);
    push.success({message: `${props.documentLabel} ${parts.join(' and ')}`});

    emit('sent');
    open.value = false;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 422 && error.response.data?.errors) {
      const errors = error.response.data.errors as Record<string, string[]>;
      fieldErrors.value = {
        email: errors.email?.[0],
        phone: errors.phone?.[0],
      };
      submitError.value = errors.channels?.[0] ?? errors.customer?.[0] ?? null;
    } else if (isAxiosError(error) && error.response?.data?.message) {
      submitError.value = String(error.response.data.message);
    } else {
      submitError.value = 'Couldn\'t send right now. Please try again.';
    }
  } finally {
    sending.value = false;
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    note.value = '';
    void loadOptions();
  }
});

defineExpose({
  openDialog: () => {
    open.value = true;
  }
});
</script>

<style scoped>
.send-channel {
  padding: 12px 14px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
}

.send-channel__meta {
  margin-top: 4px;
}

.send-sms-preview {
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.05);
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
